/**
 * Wind Data Service
 * Fetches gridded wind vectors from Open-Meteo for wind particle visualization.
 * Uses the shared RONetworkManager for API requests.
 */

import { getNetworkManager } from "@/services/weather.service";

export interface WindGrid {
  lats: number[];
  lons: number[];
  /** U component (east-west) at each grid point, row-major [lat][lon] */
  uGrid: Float32Array;
  /** V component (north-south) at each grid point, row-major [lat][lon] */
  vGrid: Float32Array;
  /** Wind speed magnitude at each grid point, row-major [lat][lon] */
  speedGrid: Float32Array;
}

export interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

const MAX_GRID_POINTS = 100;

/**
 * Returns grid spacing in degrees based on zoom level.
 */
function getGridSpacing(zoom: number): number {
  if (zoom <= 5) return 1.5;
  if (zoom <= 7) return 1.0;
  if (zoom <= 9) return 0.5;
  if (zoom <= 11) return 0.25;
  return 0.125;
}

/**
 * Build a grid of lat/lng points covering the given bounds with padding.
 */
function buildGridPoints(
  bounds: ViewportBounds,
  zoom: number,
): { lats: number[]; lons: number[] } {
  const spacing = getGridSpacing(zoom);

  // Pad bounds by one grid step to ensure coverage during panning
  const south = Math.floor((bounds.south - spacing) / spacing) * spacing;
  const north = Math.ceil((bounds.north + spacing) / spacing) * spacing;
  const west = Math.floor((bounds.west - spacing) / spacing) * spacing;
  const east = Math.ceil((bounds.east + spacing) / spacing) * spacing;

  const lats: number[] = [];
  const lons: number[] = [];

  for (let lat = south; lat <= north; lat += spacing) {
    lats.push(Math.round(lat * 1000) / 1000);
  }
  for (let lon = west; lon <= east; lon += spacing) {
    lons.push(Math.round(lon * 1000) / 1000);
  }

  // Cap grid size
  const totalPoints = lats.length * lons.length;
  if (totalPoints > MAX_GRID_POINTS) {
    // Include both outer coordinates so the grid still covers the viewport.
    // Count the retained endpoints in the budget, including on thin viewports.
    let step = Math.ceil(Math.sqrt(totalPoints / MAX_GRID_POINTS));
    const count = (length: number) => Math.ceil((length - 1) / step) + 1;
    while (count(lats.length) * count(lons.length) > MAX_GRID_POINTS) step++;
    const reduce = (values: number[]) =>
      values.filter((_, i) => i % step === 0 || i === values.length - 1);
    return { lats: reduce(lats), lons: reduce(lons) };
  }

  return { lats, lons };
}

/**
 * Convert wind speed (mph) and direction (degrees) to U/V components.
 * Direction is meteorological: degrees FROM which wind blows (0=N, 90=E).
 */
function windToUV(
  speed: number,
  directionDeg: number,
): { u: number; v: number } {
  const rad = (directionDeg * Math.PI) / 180;
  return {
    u: -speed * Math.sin(rad),
    v: -speed * Math.cos(rad),
  };
}

/**
 * Fetch wind grid data from Open-Meteo for the given viewport.
 * Uses the shared RONetworkManager for consistent endpoint/header handling.
 */
export async function fetchWindGrid(
  bounds: ViewportBounds,
  zoom: number,
): Promise<WindGrid | null> {
  const { lats, lons } = buildGridPoints(bounds, zoom);

  if (lats.length === 0 || lons.length === 0) return null;

  // Build flat arrays of all grid point coordinates (row-major: lats vary slowest)
  const allLats: number[] = [];
  const allLons: number[] = [];
  for (const lat of lats) {
    for (const lon of lons) {
      allLats.push(lat);
      allLons.push(lon);
    }
  }

  try {
    const networkManager = getNetworkManager();
    const results = await networkManager.fetchWindGrid(allLats, allLons);

    if (!results) return null;

    const totalPoints = lats.length * lons.length;
    const uGrid = new Float32Array(totalPoints);
    const vGrid = new Float32Array(totalPoints);
    const speedGrid = new Float32Array(totalPoints);

    for (let i = 0; i < totalPoints; i++) {
      const result = results[i];
      if (!result) {
        uGrid[i] = 0;
        vGrid[i] = 0;
        speedGrid[i] = 0;
        continue;
      }

      const speed = result.wind_speed_10m ?? 0;
      const direction = result.wind_direction_10m ?? 0;
      const { u, v } = windToUV(speed, direction);

      uGrid[i] = u;
      vGrid[i] = v;
      speedGrid[i] = speed;
    }

    const grid: WindGrid = { lats, lons, uGrid, vGrid, speedGrid };

    return grid;
  } catch (err: any) {
    console.warn("[WindData] Fetch failed:", err.message);
    return null;
  }
}
